import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote, ShieldCheck, Sun } from 'lucide-react';
import { fetchPublicTestimonials } from '../Services/userServices';

// 🌟 Default Fallback Data (In case backend is offline)
const DEFAULT_TESTIMONIALS = [
  {
    id: 1,
    name: 'Rajesh Sharma',
    role: 'Homeowner, Bhopal',
    rating: 5,
    review: 'DevDeepam Solar installed a 5kW rooftop system at my residence. Electricity bill dropped by almost 85%! Extremely satisfied with their subsidy assistance and quick installation.',
  },
  {
    id: 2,
    name: 'Anil Verma',
    role: 'Factory Owner, Indore',
    rating: 5,
    review: 'We opted for a 100kW commercial solar plant. The engineering team was highly professional and completed the DISCOM net-metering without any hassle.',
  },
  {
    id: 3,
    name: 'Priya Patel',
    role: 'Villa Owner, Jabalpur',
    rating: 5,
    review: 'Best solar inverter and panel quality. Their team handled all paperwork for the central government subsidy. Truly a 100% stress-free experience.',
  },
  {
    id: 4,
    name: 'Vikram Singh',
    role: 'Commercial Complex, Gwalior',
    rating: 5,
    review: 'Outstanding post-installation support and maintenance service. Our energy savings started right from the first month of grid synchronization.',
  },
];

const Testimonials = () => {
  const [allTestimonials, setAllTestimonials] = useState(DEFAULT_TESTIMONIALS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  const VISIBLE_COUNT = 4;
  const AUTO_SLIDE_INTERVAL = 4500; 

  // Window resize handler for carousel responsiveness
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch Public Testimonials from API with Fallback Protection
  useEffect(() => {
    const loadTestimonials = async () => {
      setIsLoading(true);
      try {
        const result = await fetchPublicTestimonials();
        if (result && result.success && result.data && result.data.length > 0) {
          setAllTestimonials(result.data);
        } else {
          setAllTestimonials(DEFAULT_TESTIMONIALS);
        }
      } catch (err) {
        setAllTestimonials(DEFAULT_TESTIMONIALS);
      } finally {
        setIsLoading(false);
      }
    };
    loadTestimonials();
  }, []);

  // Auto Slider Timer
  useEffect(() => {
    if (allTestimonials.length <= 1 || isPaused) return; 

    const interval = setInterval(() => {
      handleNext();
    }, AUTO_SLIDE_INTERVAL);

    return () => clearInterval(interval);
  }, [allTestimonials.length, isPaused, currentIndex]);

  // Reset Carousel for Smooth Loop
  useEffect(() => {
    if (currentIndex === allTestimonials.length && allTestimonials.length > 0) {
      const timer = setTimeout(() => {
        setIsTransitioning(false); 
        setCurrentIndex(0);
      }, 600); 
      return () => clearTimeout(timer);
    }
  }, [currentIndex, allTestimonials.length]);

  const handleNext = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % (allTestimonials.length + 1));
  };

  const handlePrev = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => {
      if (prev === 0) return allTestimonials.length - 1;
      return prev - 1;
    });
  };

  const getExtendedTestimonials = () => {
    if (allTestimonials.length === 0) return DEFAULT_TESTIMONIALS;
    if (allTestimonials.length <= VISIBLE_COUNT) return allTestimonials;
    return [...allTestimonials, ...allTestimonials.slice(0, VISIBLE_COUNT)];
  };

  const extendedTestimonials = getExtendedTestimonials();

  // Carousel width calculations tailored specifically to prevent overflows on mobile
  let translationStep = "calc(100% + 24px)";
  if (windowWidth >= 1024) {
    translationStep = "calc(25% + 6px)";
  } else if (windowWidth >= 768) {
    translationStep = "calc(50% + 12px)";
  }

  // ⏳ Skeleton Loading (Fully Responsive Structure)
  if (isLoading) {
    return (
      <section id="testimonials" className="py-12 sm:py-20 bg-[#F0F4F8] font-sans border-t border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center mb-12 text-center animate-pulse">
            <div className="h-6 w-36 bg-slate-200 rounded-full mb-4" />
            <div className="h-10 w-64 bg-slate-200 rounded-lg" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-3xl p-5 sm:p-6 border border-slate-200 shadow-sm space-y-6">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, idx) => <div key={idx} className="w-4 h-4 bg-slate-200 rounded-full" />)}
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-slate-200 rounded w-full" />
                  <div className="h-3 bg-slate-200 rounded w-5/6" />
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <div className="w-10 h-10 bg-slate-200 rounded-full" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-3 bg-slate-200 rounded w-2/3" />
                    <div className="h-2 bg-slate-200 rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="testimonials" className="py-12 sm:py-20 bg-[#F0F4F8] relative overflow-hidden font-sans border-t border-slate-200/50">
      
      {/* 🌟 Ambient Background Glows 🌟 */}
      <div className="absolute top-1/4 -left-20 w-[250px] sm:w-[450px] h-[250px] sm:h-[450px] bg-[#1E5CB3]/5 rounded-full blur-[80px] sm:blur-[110px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-20 w-[250px] sm:w-[450px] h-[250px] sm:h-[450px] bg-[#F7941D]/5 rounded-full blur-[80px] sm:blur-[110px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* 🌟 1. HEADER 🌟 */}
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 sm:gap-8 mb-10 sm:mb-16">
          <div className="text-left max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#F0F7FF] border border-[#1E5CB3]/20 px-3.5 py-1 sm:px-4 sm:py-1.5 rounded-full mb-3 sm:mb-4 shadow-sm">
              <Sun className="w-3.5 h-3.5 text-[#F7941D]" />
              <span className="text-[9px] sm:text-[10px] font-bold tracking-widest text-[#0D3F7F] uppercase">
                Customer Testimonials
              </span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-[#0F172A] tracking-tight leading-tight sm:leading-[1.15]">
              Endorsed by Over <br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent drop-shadow-sm">
                500+ Homes & Corporates
              </span>
            </h2>
          </div>

          {/* Ratings & Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 w-full lg:w-auto justify-between">
            {/* Trust badge */}
            <div className="flex items-center gap-2.5 bg-white border border-[#1E5CB3]/10 px-4 py-2 rounded-xl sm:rounded-2xl shadow-sm self-start">
              <div className="flex gap-0.5 text-[#FFC107]">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-[#FFC107] text-[#FFC107]" />)}
              </div>
              <div className="text-xs sm:text-[13px] font-bold text-[#0D3F7F]">
                4.9/5 <span className="text-slate-400 font-normal">Rating</span>
              </div>
            </div>

            {/* Slide Arrows */}
            <div className="flex gap-3 sm:gap-2 self-end sm:self-auto">
              <button
                onClick={handlePrev}
                className="w-11 h-11 sm:w-10 sm:h-10 rounded-full bg-white border border-[#1E5CB3]/20 text-[#0D3F7F] hover:text-white hover:bg-[#F7941D] hover:border-[#F7941D] flex items-center justify-center transition-all shadow-sm active:scale-95 touch-manipulation"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5 stroke-[2.5]" />
              </button>
              <button
                onClick={handleNext}
                className="w-11 h-11 sm:w-10 sm:h-10 rounded-full bg-white border border-[#1E5CB3]/20 text-[#0D3F7F] hover:text-white hover:bg-[#F7941D] hover:border-[#F7941D] flex items-center justify-center transition-all shadow-sm active:scale-95 touch-manipulation"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5 stroke-[2.5]" />
              </button>
            </div>
          </div>
        </div>

        {/* 🌟 2. CAROUSEL WRAPPER 🌟 */}
        <div className="w-full overflow-hidden py-2 px-1">
           <motion.div
            className="flex gap-6 cursor-grab active:cursor-grabbing"
            onMouseEnter={() => setIsPaused(true)}  
            onMouseLeave={() => setIsPaused(false)}  
            animate={{ x: `calc(-${currentIndex} * ${translationStep})` }}
            transition={
              isTransitioning 
                ? { duration: 0.7, ease: [0.16, 1, 0.3, 1] } 
                : { duration: 0 }
            }
          >
            {extendedTestimonials.map((t, idx) => (
              <div
                key={`${t.id}-${idx}`}
                className="w-full md:w-[calc(50%-12px)] lg:w-[calc(25%-18px)] flex-shrink-0 bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-7 border border-[#1E5CB3]/10 shadow-[0_4px_25px_rgba(13,63,127,0.03)] hover:shadow-[0_20px_40px_rgba(247,148,29,0.1)] hover:border-[#F7941D]/30 transition-all duration-300 relative flex flex-col justify-between group"
              >
                {/* Accent Background Quote Icon */}
                <span className="absolute top-5 right-5 sm:top-6 sm:right-6 text-slate-100 group-hover:text-[#F7941D]/10 transition-colors pointer-events-none">
                  <Quote className="w-7 h-7 sm:w-8 sm:h-8 rotate-180" />
                </span>
                
                <div className="space-y-3.5 sm:space-y-4">
                  {/* Rating Stars */}
                  <div className="flex gap-1 bg-[#F0F7FF] px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-lg border border-[#1E5CB3]/10 inline-flex w-fit">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${i < (t.rating || 5) ? 'text-[#FFC107] fill-[#FFC107]' : 'text-slate-200'}`} 
                      />
                    ))}
                  </div>
                  
                  {/* Review Text */}
                  <p className="text-slate-600 font-medium text-xs sm:text-[13px] leading-relaxed italic line-clamp-4 sm:line-clamp-5">
                    "{t.review}"
                  </p>
                </div>

                {/* Profile Box */}
                <div className="flex items-center gap-3 pt-4 mt-4 border-t border-slate-100">
                  {t.image ? (
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden shadow-sm border border-slate-200 shrink-0">
                      <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#0D3F7F]/10 border border-[#0D3F7F]/20 flex items-center justify-center text-[#0D3F7F] text-xs sm:text-sm font-black shrink-0 shadow-sm">
                      {(t.name || 'C').charAt(0).toUpperCase()}
                    </div>
                  )}
                  
                  <div className="min-w-0 text-left">
                    <h4 className="font-bold text-xs sm:text-[14px] text-[#0F172A] truncate flex items-center gap-1">
                      <span>{t.name}</span>
                      <ShieldCheck className="w-3.5 h-3.5 text-[#F7941D] shrink-0" />
                    </h4>
                    <div className="flex items-center gap-1 truncate mt-0.5">
                      <span className="text-[9px] uppercase font-bold text-slate-400">{t.role || 'Solar Customer'}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-300" />
                      <span className="text-[8px] sm:text-[9px] font-extrabold text-[#F7941D]">Verified</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Dynamic Pagination Dots */}
        <div className="flex justify-center gap-1.5 sm:gap-2 mt-6 sm:mt-8">
          {allTestimonials.slice(0, allTestimonials.length <= VISIBLE_COUNT ? allTestimonials.length : VISIBLE_COUNT).map((_, idx) => {
            const isActive = currentIndex % allTestimonials.length === idx;
            return (
              <button
                key={idx}
                onClick={() => {
                  setIsTransitioning(true);
                  setCurrentIndex(idx);
                }}
                className={`h-2 rounded-full transition-all duration-500 touch-manipulation ${
                  isActive 
                    ? 'w-6 sm:w-8 bg-gradient-to-r from-[#F7941D] to-[#FFC107]' 
                    : 'w-2 bg-[#0D3F7F]/20 hover:bg-[#0D3F7F]/40'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default Testimonials;